import { createContext } from 'react';
import { decorate, observable, action, computed, toJS } from 'mobx';

import * as fn from '../utilities/_functions';
import api from '../api';

export class BookingTypeUpdate {
    data = null;
    users = [];
    newBookingType = {
        code: null,
        name: null,
        subtitle: null,
        description: null,
        isRequestOnly: false,
        isHidden: false,
        anyoneAvailableEnabled: false,
        anyoneAvailableLabel: null,
        sexQuestion: null,
        genderQuestion: null,
        pronounQuestion: null,
        dateOfBirthQuestion: null,
        healthCardNumberQuestion: null,
        phoneQuestion: null,
        isNewOrReturnQuestion: null,
        messageQuestion: null,
        customQuestions: [],
    };
    selectedBookingTypeId = null;
    hasUnsavedChanges = false;
    isLoading = false;
    isSaving = false;
    isReady = false;
    cancelBookingTypeGet = null;
    cancelBookingUserGet = null;
    cancelBookingTypeDelete = null;
    cancelBookingTypeUpdate = null;

    initialize = () => {
        const that = this;

        this.clear();
        this.isReady = false;

        return new Promise((resolve, reject) => {
            Promise.all([
                that.refresh(),
                that.loadUsers(),
            ])
                .then(() => {
                    resolve();
                })
                .catch((error) => {
                    reject(error);
                })
                .finally(() => {
                    that.isReady = true;
                })
        })
    }

    refresh = () => {
        const that = this;

        return new Promise((resolve, reject) => {
            that.isLoading = true;
            api.BookingTypes.all(
                that.id,
                (c) => { that.cancelBookingTypeGet = c; }
            )
                .then(({ data }) => {
                    that.data = data;
                    resolve();
                })
                .catch((error) => {
                    reject(error);
                })
                .finally(() => {
                    that.isLoading = false;
                })
        })
    }

    loadUsers = () => {
        const that = this;

        return new Promise((resolve, reject) => {
            api.Users.search({
                parameters: [
                    { field: 'AcceptBooking', value: true },
                    { field: 'AcceptOnlineBooking', value: true },
                    { field: 'IsHidden', value: false },
                    { field: 'DeactivatedDateUtc', value: null },
                ],
                sortByFields: [
                    { field: 'Salutation', direction: 'ASC' },
                    { field: 'FirstName', direction: 'ASC' },
                    { field: 'LastName', direction: 'ASC' }
                ],
                loadProperties: true,
                includeAdministrators: false,
            }, (c) => { that.cancelBookingUserGet = c; })
                .then(({ data }) => {
                    that.users = data && data.result && data.result.length > 0 ? data.result : [];
                    resolve();
                })
                .catch((error) => {
                    reject(error);
                })
        })
    }

    new = (notify) => {
        const that = this;

        if (!!notify) {
            this.isSaving = true;
        }

        return new Promise((resolve, reject) => {
            if (that.hasUnsavedChanges) {
                const option = toJS(that.newBookingType);

                api.BookingTypes.create(option, (c) => { that.cancelBookingTypeUpdate = c; })
                    .then(({ data }) => {
                        that.clearNewBookingType();
                        that.hasUnsavedChanges = false;
                        that.refresh()
                            .then(() => {
                                resolve(data);
                            })
                            .catch((error) => {
                                reject(error);
                            })
                            .finally(() => {
                                that.isSaving = false;
                            })
                    })
                    .catch((error) => {
                        reject(error);
                    })
            }
            else {
                that.isSaving = false;
                resolve();
            }
        })
    }

    sort = (notify) => {
        const that = this;

        if (!!notify) {
            this.isSaving = true;
        }

        return new Promise((resolve, reject) => {
            if (that.hasUnsavedChanges) {
                Promise.all(that.publicBookingTypes.map(d => {
                    return api.BookingTypes.update(d.id, toJS(d))
                }))
                    .then(() => {
                        that.hasUnsavedChanges = false;
                        that.refresh();
                        resolve();
                    })
                    .catch((error) => {
                        reject(error);
                    })
                    .finally(() => {
                        that.isSaving = false;
                    })
            }
            else {
                that.isSaving = false;
                resolve();
            }
        })
    }

    update = (notify) => {
        const that = this;

        if (!!notify) {
            this.isSaving = true;
        }

        return new Promise((resolve, reject) => {
            if (that.hasUnsavedChanges) {
                const option = toJS(that.selectedBookingType);
                delete option.users;
                api.BookingTypes.update(that.selectedBookingTypeId, option, (c) => { that.cancelBookingTypeUpdate = c; })
                    .then(({ data }) => {
                        that.hasUnsavedChanges = false;
                        that.refresh();
                        resolve(data);
                    })
                    .catch((error) => {
                        reject(error);
                    })
                    .finally(() => {
                        that.isSaving = false;
                    })
            }
            else {
                that.isSaving = false;
                resolve();
            }
        })
    }

    updateUsers = (notify) => {
        const that = this;

        if (!!notify) {
            this.isSaving = true;
        }

        return new Promise((resolve, reject) => {
            if (that.hasUnsavedChanges) {
                const option = {
                    users: that.selectedBookingTypeActiveUsers.map(u => {
                        return {
                            userId: u.id,
                            cutoffHours: u.cutoffHours,
                            displayOrder: u.displayOrder,
                        }
                    })
                };
                api.BookingTypes.updateUsers(that.selectedBookingTypeId, option, (c) => { that.cancelBookingTypeUpdate = c; })
                    .then(({ data }) => {
                        that.hasUnsavedChanges = false;
                        that.refresh();
                        resolve(data);
                    })
                    .catch((error) => {
                        reject(error);
                    })
                    .finally(() => {
                        that.isSaving = false;
                    })
            }
            else {
                that.isSaving = false;
                resolve();
            }
        })
    }

    delete = (notify) => {
        const that = this;

        if (!!notify) {
            this.isSaving = true;
        }

        return new Promise((resolve, reject) => {
            api.BookingTypes.delete(
                that.selectedBookingTypeId,
                (c) => { this.cancelBookingTypeDelete = c; }
            )
                .then(() => {
                    that.refresh()
                        .then(() => {
                            resolve();
                        })
                        .catch((error) => {
                            reject(error);
                        })
                        .finally(() => {
                            that.isSaving = false;
                        })
                })
                .catch((error) => {
                    reject(error);
                })
        })
    }

    clearNewBookingType = () => {
        this.newBookingType = {
            code: null,
            name: null,
            subtitle: null,
            description: null,
            isRequestOnly: false,
            isHidden: false,
            anyoneAvailableEnabled: false,
            anyoneAvailableLabel: null,
            sexQuestion: null,
            genderQuestion: null,
            pronounQuestion: null,
            dateOfBirthQuestion: null,
            healthCardNumberQuestion: null,
            phoneQuestion: null,
            isNewOrReturnQuestion: null,
            messageQuestion: null,
            customQuestions: [],
        };
    }

    clear = () => {
        this.clearNewBookingType();
        this.data = null;
        this.hasUnsavedChanges = false;
        this.isLoading = false;
        this.isSaving = false;
        this.isReady = false;

        if (fn.isFunction(this.cancelBookingTypeGet)) {
            this.cancelBookingTypeGet();
            this.cancelBookingTypeGet = null;
        }

        if (fn.isFunction(this.cancelBookingTypeDelete)) {
            this.cancelBookingTypeDelete();
            this.cancelBookingTypeDelete = null;
        }

        if (fn.isFunction(this.cancelBookingTypeUpdate)) {
            this.cancelBookingTypeUpdate();
            this.cancelBookingTypeUpdate = null;
        }
    }

    get selectedBookingType() {
        const that = this;
        if (!that.data || !that.selectedBookingTypeId) return null;
        return that.data.find(d => d.id === that.selectedBookingTypeId);
    }

    get selectedBookingTypeActiveUsers() {
        const that = this;
        if (!that.selectedBookingType) return null;
        return that.selectedBookingType.users && that.selectedBookingType.users.length > 0 ? that.selectedBookingType.users : [];
    }

    get selectedBookingTypeInactiveUsers() {
        const that = this;
        if (!that.selectedBookingType) return that.users;
        return that.selectedBookingType.users && that.selectedBookingType.users.length > 0 ? that.users.filter(u1 => !that.selectedBookingType.users.some(u2 => u1.id === u2.id)) : that.users;
    }

    get publicBookingTypes() {
        const that = this;
        if (that.data && that.data.length > 0) {
            return that.data.some(d => !d.isHidden) ? that.data.filter(d => !d.isHidden) : [];
        }
        return [];
    }

    get privateBookingTypes() {
        const that = this;
        if (that.data && that.data.length > 0) {
            return that.data.some(d => !!d.isHidden) ? that.data.filter(d => !!d.isHidden) : [];
        }
        return [];
    }
}

decorate(BookingTypeUpdate, {
    data: observable,
    newBookingType: observable,
    selectedBookingTypeId: observable,
    hasUnsavedChanges: observable,
    isLoading: observable,
    isSaving: observable,
    isReady: observable,
    initialize: action,
    refresh: action,
    new: action,
    sort: action,
    update: action,
    updateUsers: action,
    delete: action,
    clear: action,
    selectedBookingType: computed,
    selectedBookingTypeActiveUsers: computed,
    selectedBookingTypeInactiveUsers: computed,
    publicBookingTypes: computed,
    privateBookingTypes: computed,
})

export default createContext(new BookingTypeUpdate());